import { customElement } from 'lit/decorators.js';
import { TuftsYext } from '../tufts-yext/tufts-yext';
import { CSSResultGroup, TemplateResult, html, noChange } from 'lit';
import StyleTuftsButton from '../tufts-button/tufts-button.css.lit';
import StyleTuftsYext from '../tufts-yext/tufts-yext.css.lit';
import componentStyles from './tufts-yext-print.css.lit';
import { classMap } from 'lit/directives/class-map.js';
import { repeat } from 'lit/directives/repeat.js';
import {
  HealthcareProfessional,
  verticalSearchResponseStructure,
} from '../tufts-yext/tufts-yext-types';
import { formatPhone } from '../tufts-yext/tufts-yext-format';
import { ifDefined } from 'lit/directives/if-defined.js';
import { defaultImageTemplate } from '../tufts-yext/image-template';

@customElement('tufts-yext-print')
export class TuftsYextPrint extends TuftsYext {
  static styles: CSSResultGroup = [
    StyleTuftsButton,
    StyleTuftsYext,
    componentStyles,
  ];

  showAllDataProviders = false;

  connectedCallback() {
    super.connectedCallback();
    this.hideHeaderAndFooter();
    this.appendShoelaceScript();
  }

  appendShoelaceScript() {
    // Create a new script element
    const script = document.createElement('script');
    script.type = 'module';
    script.src =
      'https://cdn.jsdelivr.net/npm/@shoelace-style/shoelace@2.15.1/cdn/shoelace.js';

    // Append the script to the document head
    document.head.appendChild(script);
  }

  hideHeaderAndFooter() {
    // Helper function to hide an element by selector
    const hideElementBySelector = (selector: string) => {
      const element = document.querySelector<HTMLElement>(selector);
      if (element) {
        element.style.display = 'none';
      }
    };
    // Hide the header and footer elements
    hideElementBySelector('outline-header');
    hideElementBySelector('tufts-footer');
    hideElementBySelector('tufts-breadcrumbs');
  }

  firstUpdated() {
    setTimeout(() => {
      this.createQrCodeImage();
    }, 1000);
  }

  createQrCodeImage() {
    // Get the 'sl-qr-code' element and the 'img' element
    const qrCodeElement = this.shadowRoot?.querySelector('sl-qr-code');
    const qrCodeImageElement = this.shadowRoot!.querySelector(
      '#qrCodeImage'
    ) as HTMLImageElement;
    const canvas = qrCodeElement?.shadowRoot?.querySelector('canvas');
    // If the canvas and qrCodeElement exist
    if (canvas && qrCodeElement) {
      // Set the width of the qrCodeImageElement to 100px
      qrCodeImageElement.style.width = '100px';
      // Set the source of the qrCodeImageElement to the data URL of the canvas
      qrCodeImageElement.src = canvas.toDataURL();
      // Set the 'hidden' attribute of the qrCodeElement
      qrCodeElement.setAttribute('hidden', '');
    }

    // After 2 seconds, print the window
    setTimeout(() => {
      window.print();
    }, 2500);
  }

  headingAndBreadcrumbsTemplate(): TemplateResult {
    return html` <tufts-breadcrumbs>
        <nav class="breadcrumb">
          <ol slot="breadcrumb">
            <li>
              <a href="/">Home</a>
            </li>
          </ol>
        </nav>
      </tufts-breadcrumbs>
      <tufts-heading level-size="xxl">
        <h1>Print Doctor Results</h1>
      </tufts-heading>`;
  }

  distanceProfileTemplate() {
    return html``;
  }

  displayAll(response: verticalSearchResponseStructure) {
    if (response.resultsCount === 0) {
      return html` <h2>No results found</h2> `;
    }

    // this.randomizationToken = response.randomizationToken;
    // Set user location from response or use defaults
    this.setUserLocation(response);

    return html`
      <tufts-grid
        justify-self="center"
        col-gap="2rem"
        row-gap="2rem"
        item-min-width="200px"
        columns="3"
        align-self=""
      >
        ${repeat(
          response.results,
          result => result,
          (result, index) => html`
            <li class="profile">
              ${this.profileTemplate(
                result.data as HealthcareProfessional,
                index
              )}
            </li>
          `
        )}
      </tufts-grid>
      ${this.gridTEmplateListProviders(response)}
    `;
  }
  gridTEmplateListProviders(response: verticalSearchResponseStructure) {
    // Calculate the midpoint to divide the results into two halves
    const half = Math.ceil(response.results.length / 2);
    const firstHalf = response.results.slice(0, half);
    const secondHalf = response.results.slice(half);

    return html`
      <div class="provider-list">
        <div class="column">
          ${repeat(
            firstHalf,
            result => result,
            result => html`
              <div class="provider-item">
                ${this.listTemplate(result.data as HealthcareProfessional)}
              </div>
            `
          )}
        </div>
        <div class="column">
          ${repeat(
            secondHalf,
            result => result,
            result => html`
              <div class="provider-item">
                ${this.listTemplate(result.data as HealthcareProfessional)}
              </div>
            `
          )}
        </div>
      </div>
    `;
  }
  listTemplate(data: HealthcareProfessional) {
    const addressString = data.address
      ? `${data.address.line1} ${data.address.city}, ${data.address.region} ${data.address.postalCode}`
      : null;
    return html`
      <div class="profile">
        <p class="title-additional-profile">${data.name}</p>
        <div class="additional-provider-data">
          ${data.officeName ? html`<span>${data.officeName}</span>` : null}
          ${data.mainPhone
            ? html` <span class="visually-hidden">Phone Number</span>
                <a data-analytics="TAP_TO_CALL" href="tel:${data.mainPhone}">
                  ${formatPhone(data.mainPhone, true)}
                </a>`
            : html`<br />`}
          ${addressString
            ? html`
                <a
                  data-analytics="DRIVING_DIRECTIONS"
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://www.google.com/maps/search/?api=1&amp;map_action=map&amp;query=${encodeURI(
                    addressString
                  )}"
                >
                  ${addressString}
                </a>
              `
            : null}
        </div>

        ${data.c_additionalProfiles?.length > 0
          ? repeat(
              data.c_additionalProfiles,
              profile => profile,
              (profile, index) =>
                html`<div class="additional-provider-data" data-index=${index}>
                  ${profile.officeName
                    ? html`<span>${profile.officeName}</span>`
                    : null}
                  ${profile.phoneNumber
                    ? html` <span class="visually-hidden">Phone Number</span>
                        <a
                          data-analytics="TAP_TO_CALL"
                          href="tel:${profile.phoneNumber}"
                        >
                          ${formatPhone(profile.phoneNumber, true)}
                        </a>`
                    : null}
                  <a
                    data-analytics="DRIVING_DIRECTIONS"
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://www.google.com/maps/search/?api=1&amp;map_action=map&amp;query=${encodeURI(
                      profile.complement
                    )}"
                  >
                    ${profile.complement}
                  </a>
                </div>`
            )
          : null}
      </div>
    `;
  }
  createLinkToTheQRCode(): string {
    const currentUrl = new URL(window.location.href);
    const storybookUrl = currentUrl.search.split('viewMode=story')[1];
    return this.debug
      ? `${currentUrl.origin}/storybook/iframe.html?id=content-yext-yext-list--tufts-yext&viewMode=story${storybookUrl}`
      : `${currentUrl.origin}/find-a-doctor/${currentUrl.search}`;
  }

  pillsAndModalTemplate() {
    return html`
      <div class="filters-wrapper-desktop">
        <div class="filters-wrapper">
          <ul class="filters-container" aria-live="polite">
            ${this.fetchEndpoint.render({
              pending: () => (this.taskValue ? noChange : null),
              complete: data => html`
                ${this.displayFacetsActive(data.response)}
              `,
              error: error => html`${error}`,
            })}
          </ul>
        </div>
        <form class="tufts-form">
          <select
            data-drupal-selector="edit-select-list"
            name="select_list[]"
            id="edit-select-list"
            hidden
            class="form-select news-media-search "
            aria-label="Sort By"
            @change="${(e: Event) => this.handleSort(e)}"
          >
            <option value="relevance">Relevance</option>
            <option value="entity_distance">Distance</option>
          </select>
        </form>
      </div>
    `;
  }

  templateCTAProfileAndSchedule() {
    return html``;
  }
  displayFilterPill(
    facets: verticalSearchResponseStructure['facets'],
    parentAsLabel: Boolean
  ) {
    return facets.map(facet => {
      return facet.options.map(option => {
        if (!option.selected) return;
        const idWithSpaces = facet.displayName + `-` + option.displayName;
        const id = idWithSpaces.replace(/\s/g, '_');
        return html`<li class="filter-option">
          <div class="btn compact medium" id=${`button-${id}`}>
            <span class="visually-hidden">Unselect</span>
            ${parentAsLabel ? option.displayName : facet.displayName}
            <span class="visually-hidden">filter</span>
          </div>
        </li>`;
      });
    });
  }
  headerTopTemplate() {
    return html` <div class="top-header">
      <div class="logo">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 251 32"
          width="251"
          height="32"
          fill="none"
        >
          <path
            fill="currentColor"
            d="M.91.22h27.2V8.9h-4.3V5.2h-5.78v21.89h4.67v4.38H6.26V27.1h4.67V5.2H5.27v7.15H.9V.22ZM36.7 10.28v4.13h3.33v10.76l-.2.15c-1.46 1.12-2.85 1.69-4.12 1.69-1 0-1.74-.27-2.16-.8-.42-.53-.61-1.35-.61-2.6V10.28h-9.14v4.13h3.25v11.03c0 2.04.55 3.63 1.63 4.73 1.08 1.1 2.61 1.65 4.56 1.65a10.4 10.4 0 0 0 6.02-2.11l.77-.55v2.34h8.33v-4.1h-2.41V10.27h-9.25Zm17.79-8.09a7.99 7.99 0 0 0-1.98 5.53v2.55h-2.9v4.13h2.9v12.98h-2.91v4.11h12.24v-4.1h-3.4V14.4h3.4v-4.13h-3.4V7.33c0-2.97 1.25-3.6 2.29-3.6.92 0 1.65.7 2.16 2.07l4.06-3.26C65.55.87 63.37 0 60.4 0c-2.63 0-4.63.74-5.91 2.2h-.02Zm15.45 1.3-4.1 3.04v3.74h-2.76v4.13h2.75v12.14c0 3.6 1.65 5.29 5.19 5.29a12.78 12.78 0 0 0 4.71-.98v-3.9a6.14 6.14 0 0 1-1.69.3c-.95 0-1.56-.22-1.89-.66-.32-.44-.42-1.05-.42-2.06V14.42h3.98v-4.13h-3.97V2.17L69.9 3.51l.02-.02Zm8.2 7.86a5.57 5.57 0 0 0-1.54 1.96 5.73 5.73 0 0 0-.54 2.46 5.47 5.47 0 0 0 .56 2.52 5.3 5.3 0 0 0 1.64 1.95c1.5 1.15 4 2.02 7.45 2.6 1.74.3 2.9.61 3.51.96.31.14.57.36.75.65.19.3.28.63.28.97 0 .38-.1.74-.3 1.06a2.06 2.06 0 0 1-.77.75 5.2 5.2 0 0 1-2.81.65 5.98 5.98 0 0 1-3.5-.97 3.31 3.31 0 0 1-1-1.05 3.4 3.4 0 0 1-.48-1.4h-4.5v7.04h4.45v-1.6l.52.59c.89 1 2.6 1.51 5.07 1.51 2.68 0 4.77-.67 6.22-2a6.56 6.56 0 0 0 1.64-2.3 6.72 6.72 0 0 0 .53-2.81 6.26 6.26 0 0 0-1.14-3.76 6.34 6.34 0 0 0-3.04-2.25 39.48 39.48 0 0 0-5.94-1.4 14.05 14.05 0 0 1-3.23-.83 1.34 1.34 0 0 1-.57-.5 1.39 1.39 0 0 1-.2-.75c0-1.33 1.1-2.01 3.29-2.01 2.7 0 4.36.78 4.93 2.32h4.45V9.87h-4.34v.92l-.42-.18a13.4 13.4 0 0 0-5.35-1.06c-2.33 0-4.22.62-5.62 1.82v-.02Zm35.6 20.14 9.69-26.77v26.75h3.18V.22h-4.81L113 24.96 104.22.22H99.4v31.25h3.19V4.7l9.68 26.77 1.46.02ZM139.3 9.75c4.8 0 9.34 2.98 9.34 10.63v1.1h-15.66c.15 4.87 2.42 7.56 6.67 7.56 3.22 0 5.1-1.22 5.53-3.72h3.3c-.72 4.3-4.18 6.54-8.87 6.54-5.88 0-10.05-4.25-10.05-10.85v-.33c0-6.45 4.01-10.95 9.74-10.95v.02Zm5.96 8.97c-.32-4.41-2.53-6.17-5.96-6.17s-5.65 2.37-6.22 6.17h12.18ZM167.89.22h3.32v31.25h-3.33v-3.7a8.72 8.72 0 0 1-7.11 4.05c-5.45 0-9.39-3.96-9.39-10.62v-.33c0-6.54 3.98-11.16 9.72-11.16 3.45 0 5.6 1.74 6.8 3.8L167.87.23Zm.11 20.4c0-5.48-2.53-8.05-6.6-8.05-4.13 0-6.6 2.98-6.6 8.17v.33c0 5.4 2.83 7.97 6.34 7.97 3.85 0 6.87-2.6 6.87-8.1v-.32Zm7.22 10.85h3.3v-21.2h-3.3v21.2Zm1.65-28.46c.43 0 .85.14 1.22.38.35.25.63.6.8 1.01a2.3 2.3 0 0 1-.48 2.45 2.17 2.17 0 0 1-1.12.62c-.43.08-.87.04-1.27-.13a2.2 2.2 0 0 1-.98-.83 2.29 2.29 0 0 1-.36-1.25 2.3 2.3 0 0 1 .65-1.59c.4-.42.96-.66 1.54-.66Zm15.03 26c-4.01 0-6.8-2.77-6.8-8v-.33c0-5.03 2.9-8.13 6.68-8.13 2.87 0 5.25 1.27 5.77 4.66h3.26c-.64-5.44-4.7-7.48-9.03-7.48-5.57 0-10.1 4.41-10.1 10.95v.33c0 6.62 4.37 10.85 10.17 10.85 5.01 0 8.63-3.43 9.07-8.05h-3.06c-.36 3.47-3.02 5.2-5.97 5.2h.01Zm13.84-26c-.43 0-.86.13-1.22.38s-.64.6-.8 1a2.31 2.31 0 0 0-.13 1.3c.09.44.3.84.6 1.16a2.16 2.16 0 0 0 2.38.49c.4-.17.75-.46.99-.83.24-.37.36-.8.36-1.25a2.3 2.3 0 0 0-.64-1.58 2.18 2.18 0 0 0-1.54-.67Zm-1.65 7.27h3.31v21.2h-3.3v-21.2Zm17.3-.55c-3.42 0-5.73 1.74-6.68 3.76v-3.21h-3.3v21.2h3.3V18.51c0-3.84 2.74-5.85 5.8-5.85 3.34 0 4.9 1.68 4.9 5.44v13.36h3.29V18.36c0-6.22-3.02-8.63-7.31-8.63Zm29.35 11.72h-15.66c.16 4.86 2.42 7.56 6.68 7.56 3.22 0 5.06-1.22 5.52-3.72h3.3c-.71 4.3-4.17 6.54-8.87 6.54-5.88 0-10.06-4.25-10.06-10.85v-.33c0-6.45 4.02-10.95 9.74-10.95 4.82 0 9.35 2.98 9.35 10.63v1.12Zm-3.38-2.74c-.32-4.41-2.53-6.17-5.96-6.17-3.44 0-5.65 2.37-6.2 6.17h12.16Z"
          />
        </svg>
      </div>
      <!-- @todo:hidden text until we can Yext filter by location
      <div class="edit-direction ">
        <span class="title-direction" contenteditable="true"
          >Tufts Medical Center editable text
        </span>
        <span class="content-direction" contenteditable="true"
          >800 Washington Street, Proger Building, 1st floor editable text</span
        >
      </div>
      -->
    </div>`;
  }
  additionalProfilesLocationTemplate(data: HealthcareProfessional) {
    return html`${data.c_additionalProfiles?.length > 0
      ? repeat(
          data.c_additionalProfiles,
          profile => profile,
          (profile, index) =>
            html`<div slot="location" data-index=${index}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M12 12C12.55 12 13.02 11.8 13.41 11.41C13.8 11.02 14 10.55 14 10C14 9.45 13.8 8.98 13.41 8.59C13.02 8.2 12.55 8 12 8C11.45 8 10.98 8.2 10.59 8.59C10.2 8.98 10 9.45 10 10C10 10.55 10.2 11.02 10.59 11.41C10.98 11.8 11.45 12 12 12ZM12 12C12.55 12 13.02 11.8 13.41 11.41C13.8 11.02 14 10.55 14 10C14 9.45 13.8 8.98 13.41 8.59C13.02 8.2 12.55 8 12 8C11.45 8 10.98 8.2 10.59 8.59C10.2 8.98 10 9.45 10 10C10 10.55 10.2 11.02 10.59 11.41C10.98 11.8 11.45 12 12 12ZM12 12C12.55 12 13.02 11.8 13.41 11.41C13.8 11.02 14 10.55 14 10C14 9.45 13.8 8.98 13.41 8.59C13.02 8.2 12.55 8 12 8C11.45 8 10.98 8.2 10.59 8.59C10.2 8.98 10 9.45 10 10C10 10.55 10.2 11.02 10.59 11.41C10.98 11.8 11.45 12 12 12Z"
                  fill="#428FEC"
                />
                <path
                  d="M12.0018 22.06L10.6518 20.82C8.47181 18.82 6.88181 17.03 5.80181 15.34C4.59181 13.47 4.01181 11.78 4.01181 10.19C4.00181 7.84 4.78181 5.86 6.32181 4.32C7.86181 2.78 9.77181 2 12.0018 2C14.2318 2 16.1318 2.78 17.6818 4.32C19.2218 5.86 20.0018 7.84 20.0018 10.2C20.0018 11.79 19.4118 13.47 18.2118 15.35C17.1218 17.04 15.5418 18.83 13.3618 20.83L12.0118 22.07L12.0018 22.06ZM12.0018 19.35C14.0218 17.49 15.5518 15.78 16.5218 14.26C17.5018 12.74 18.0018 11.38 18.0018 10.2C18.0018 8.39 17.4218 6.89 16.2618 5.74C15.1018 4.59 13.6718 4 12.0018 4C10.3318 4 8.89181 4.58 7.74181 5.74C6.59181 6.89 6.00181 8.39 6.00181 10.2C6.00181 11.38 6.50181 12.74 7.48181 14.26C8.46181 15.78 9.98181 17.49 12.0018 19.35Z"
                  fill="#00308C"
                />
              </svg>
              ${profile.officeName
                ? html`<strong>${profile.officeName}</strong><br />`
                : null}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.google.com/maps/search/?api=1&amp;map_action=map&amp;query=${encodeURI(
                  profile.complement
                )}"
              >
                ${profile.complement}
              </a>
              ${profile.phoneNumber
                ? html`<svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M18.9922 11.995C18.9922 10.0461 18.3126 8.397 16.9533 7.03776C15.5941 5.67851 13.945 4.99889 11.9961 4.99889V3C13.2454 3 14.4148 3.23987 15.5041 3.70961C16.5935 4.18934 17.543 4.82898 18.3526 5.63853C19.1621 6.44808 19.8018 7.39756 20.2815 8.48695C20.7512 9.57635 20.9911 10.7457 20.9911 11.995H18.9922Z"
                        fill="#428FEC"
                      />
                      <path
                        d="M14.9944 11.995C14.9944 11.1655 14.7046 10.4559 14.1149 9.8762C13.5252 9.29653 12.8256 8.99669 11.9961 8.99669V6.9978C13.3753 6.9978 14.5547 7.48753 15.5341 8.45699C16.5036 9.43645 16.9933 10.6058 16.9933 11.995H14.9944Z"
                        fill="#428FEC"
                      />
                      <path
                        d="M20.7701 15.2832C20.6202 15.1133 20.4303 14.9933 20.1905 14.9434L16.7424 14.2438C16.5125 14.2138 16.2726 14.2338 16.0328 14.3037C15.7929 14.3837 15.593 14.4936 15.4431 14.6435L13.0944 16.9922C11.8251 16.2227 10.6657 15.3232 9.60633 14.2737C8.54692 13.2243 7.66741 12.0949 6.9678 10.8956L9.39645 8.44697C9.54636 8.29706 9.64631 8.12715 9.68629 7.93726C9.72626 7.74736 9.73626 7.53748 9.69628 7.29761L9.04664 3.79956C9.01666 3.56968 8.90672 3.37979 8.71682 3.21988C8.53692 3.06996 8.32704 3 8.09717 3H4.04942C3.74958 3 3.49972 3.09994 3.29983 3.29983C3.09994 3.49972 3 3.74958 3 4.04942C3 6.19822 3.47973 8.29706 4.4392 10.3359C5.39867 12.3748 6.65797 14.1838 8.23709 15.7629C9.80622 17.342 11.6152 18.6013 13.6641 19.5608C15.7029 20.5203 17.8018 21 19.9506 21C20.2504 21 20.5003 20.9001 20.7002 20.7002C20.9001 20.5003 21 20.2504 21 19.9506V15.9028C21 15.673 20.92 15.4631 20.7801 15.2932L20.7701 15.2832ZM5.37868 7.02776C5.22876 6.35813 5.10883 5.68851 5.02887 4.99889H7.24764L7.66741 7.34758L6.01832 8.99667C5.73848 8.34703 5.5186 7.6874 5.36868 7.02776H5.37868ZM18.9911 18.9411C18.3115 18.8912 17.6319 18.7812 16.9522 18.6113C16.2726 18.4414 15.613 18.2215 14.9634 17.9417L16.6424 16.2726L18.9911 16.7424V18.9411Z"
                        fill="#00308C"
                      />
                    </svg>
                    <span class="visually-hidden">Phone Number</span>
                    <a href="tel:${profile.phoneNumber}">
                      ${formatPhone(profile.phoneNumber)}
                    </a>`
                : null}
            </div>`
        )
      : null}`;
  }
  profileTemplate(
    data: HealthcareProfessional,
    index: number
  ): TemplateResult | null {
    // Hide/Display "temp" / unfinished profiles
    if (this.filterTempProfiles) {
      if (data.id.startsWith('Temp-')) {
        return html`.`;
      }
    }

    // This logic was created to be able to unite the specialties and subspecialties and take only the first 3,
    // we do not know if YEXT is going to send us a new field (c_subSpecialtyDisplayName)
    // because right now it has a temporary (c_subSpecialtyDisplayName_Temporary),
    // so we do not know if it is a string or an array, that's why we are creating all this logic,
    // to be able to prevent any error because we don't know for sure what they are going to send us and in what format.
    let subSpecialty = '';
    let joinSpecialtyAndSub: string[] = [];
    if (data.c_specialtyName) {
      if (data.c_subSpecialtyDisplayName) {
        joinSpecialtyAndSub = this.handleSubSpecialty(
          data,
          data.c_subSpecialtyDisplayName
        );
      } else if (data.c_subSpecialtyDisplayName_Temporary) {
        joinSpecialtyAndSub = this.handleSubSpecialty(
          data,
          data.c_subSpecialtyDisplayName_Temporary
        );
      }
      // take only the first 3
      subSpecialty = joinSpecialtyAndSub.splice(0, 3).join(', ');
    }

    return html`
      ${this.debug
        ? html` <details class="debug">
            <summary></summary>
            <div>
              <pre>${JSON.stringify(data, null, 2)}</pre>
            </div>
          </details>`
        : null}
      <tufts-card-provider-listing print data-index=${index}>
        <div slot="photo">
          ${data.headshot?.url
            ? html`
                <img
                  src="${ifDefined(data.headshot?.url)}"
                  alt="${ifDefined(data.name)}"
                />
              `
            : defaultImageTemplate()}
        </div>
        <div slot="name">
          <h4>${data.name}</h4>
        </div>

        ${data.c_clinicalTitles
          ? html` <div slot="specialty">${data.c_clinicalTitles[0]}</div> `
          : null}
        ${subSpecialty
          ? html` <div slot="specialty">${subSpecialty}</div> `
          : null}
        ${data.acceptingNewPatients
          ? html`
              <div slot="iconprint">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M10.6016 16.5999L17.6516 9.5499L16.2516 8.1499L10.6016 13.7999L7.75156 10.9499L6.35156 12.3499L10.6016 16.5999Z"
                    fill="#428FEC"
                  />
                  <path
                    d="M21.21 8.1C20.69 6.88 19.97 5.83 19.07 4.93C18.17 4.03 17.11 3.32 15.9 2.79C14.68 2.27 13.38 2 12 2C10.62 2 9.32 2.26 8.1 2.79C6.88 3.32 5.83 4.03 4.93 4.93C4.03 5.83 3.32 6.89 2.79 8.1C2.26 9.32 2 10.62 2 12C2 13.38 2.26 14.68 2.79 15.9C3.31 17.12 4.03 18.18 4.93 19.07C5.83 19.96 6.89 20.68 8.1 21.21C9.32 21.74 10.62 22 12 22C13.38 22 14.68 21.74 15.9 21.21C17.12 20.69 18.18 19.97 19.07 19.07C19.97 18.17 20.68 17.11 21.21 15.9C21.74 14.68 22 13.38 22 12C22 10.62 21.74 9.32 21.21 8.1ZM17.67 17.68C16.12 19.23 14.23 20.01 11.99 20.01C9.75 20.01 7.87 19.23 6.32 17.68C4.77 16.13 3.99 14.24 3.99 12.01C3.99 9.78 4.77 7.89 6.32 6.34C7.87 4.79 9.76 4.01 11.99 4.01C14.22 4.01 16.11 4.79 17.67 6.34C19.22 7.89 19.99 9.78 19.99 12.01C19.99 14.24 19.22 16.13 17.67 17.68Z"
                    fill="#00308C"
                  />
                </svg>
                Accepting new patients
              </div>
            `
          : null}
        ${data.languages
          ? html`
              <div slot="iconprint">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    fill="#00308C"
                    d="M9 15.844c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4Zm-6 4c.22-.72 3.31-2 6-2 2.7 0 5.8 1.29 6 2H3ZM11.83 7.014A3.866 3.866 0 0 0 9 5.844c-1.1 0-2.04.39-2.83 1.17-.79.78-1.17 1.73-1.17 2.83s.39 2.04 1.17 2.83c.78.79 1.73 1.17 2.83 1.17s2.04-.39 2.83-1.17c.78-.78 1.17-1.73 1.17-2.83s-.39-2.04-1.17-2.83Zm-1.41 4.24c-.39.39-.86.59-1.41.59-.55 0-1.02-.2-1.41-.59-.39-.39-.59-.86-.59-1.41 0-.55.2-1.02.59-1.41.39-.39.86-.59 1.41-.59.55 0 1.02.2 1.41.59.39.39.59.86.59 1.41 0 .55-.2 1.02-.59 1.41Z"
                  />
                  <path
                    fill="#428FEC"
                    d="M15.078 11.784c.84-1.18.84-2.71 0-3.89l1.68-1.69c2.02 2.2 2.02 5.25 0 7.27l-1.68-1.69Z"
                  />
                  <path
                    fill="#428FEC"
                    d="m18.438 4.474 1.63-1.63c3.91 4.05 3.9 10.11 0 14l-1.63-1.63c2.77-3.18 2.77-7.72 0-10.74Z"
                  />
                </svg>
                ${this.speakLanguages(data.languages)}
              </div>
            `
          : null}
      </tufts-card-provider-listing>
    `;
  }
  speakLanguages(languages: string[]): string {
    if (languages.length === 1) {
      return languages[0];
    } else if (languages.length === 2) {
      return languages.join(' and ');
    } else if (languages.length > 2) {
      const lastLanguage = languages[languages.length - 1];
      return languages.slice(0, -1).join(', ') + ' and ' + lastLanguage;
    } else {
      return '';
    }
  }

  render(): TemplateResult {
    if (this.fetchEndpoint.value !== undefined) {
      this.taskValue = this.fetchEndpoint.value;
    }
    const classes = {
      section: true,
      isMobile: this.resizeController.currentBreakpointRange === 0,
    };
    return html`<table>
      <thead>
        <tr>
          <td>
            <div class="qr-overview">
              ${this.headerTopTemplate()}
              <span>
                Scan with your phone's camera to <br />
                see these clinicians on our website:</span
              >
              <sl-qr-code
                value="${this.createLinkToTheQRCode()}"
                label="Scan with your phone's camera to see these clinicians on our website"
                size="100"
              ></sl-qr-code>
              <img id="qrCodeImage" />
            </div>
          </td>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            <div class="${classMap(classes)}">
              ${this.pillsAndModalTemplate()}
              ${this.fetchEndpoint.render({
                pending: () =>
                  this.taskValue ? this.displayPending() : noChange,
                complete: data => this.displayAll(data.response),
                error: error => html`${error}`,
              })}
            </div>
          </td>
        </tr>
      </tbody>
    </table>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tufts-yext-print': TuftsYextPrint;
  }
}
